/*
  ЦИФРОВОЙ ДИАК ЗФ — INLINE EDIT COMPONENTS
  Reusable primitives for in-place editing of text, numbers, dates, selects.
*/

import React, { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── InlineText ───────────────────────────────────────────────────────────

interface InlineTextProps {
  value: string;
  onSave: (v: string) => void;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  multiline?: boolean;
  displayAs?: 'h1' | 'h2' | 'h3' | 'p' | 'span';
  readOnly?: boolean;
  /** Ограничение числа строк у текста (для компактных ячеек, напр. Ганта). */
  clampLines?: 1 | 2 | 3;
}

export function InlineText({
  value, onSave, className, inputClassName, placeholder = 'Введите текст…',
  multiline = false, displayAs: Tag = 'span', readOnly = false, clampLines,
}: InlineTextProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLTextAreaElement & HTMLInputElement>(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  const commit = () => { onSave(draft.trim() || value); setEditing(false); };
  const cancel = () => { setDraft(value); setEditing(false); };

  const handleKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) { e.preventDefault(); commit(); }
    if (e.key === 'Escape') cancel();
  };

  if (readOnly) {
    return (
      <Tag className={cn(className, 'cursor-default')}>
        {value || <span className="text-muted-foreground italic">{placeholder}</span>}
      </Tag>
    );
  }

  if (editing) {
    if (multiline) {
      return (
        <span className="flex w-full flex-col gap-2">
          <textarea
            ref={ref as React.RefObject<HTMLTextAreaElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKey}
            placeholder={placeholder}
            rows={3}
            className={cn(
              'w-full resize-y rounded border border-[var(--nn-blue)] bg-[var(--nn-blue-pale)] px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--nn-blue-light)]',
              inputClassName,
            )}
          />
          <span className="flex justify-end gap-1">
            <button type="button" onClick={commit} className="rounded p-1.5 text-green-600 hover:bg-green-50" aria-label="Сохранить">
              <Check size={14} />
            </button>
            <button type="button" onClick={cancel} className="rounded p-1.5 text-red-500 hover:bg-red-50" aria-label="Отмена">
              <X size={14} />
            </button>
          </span>
        </span>
      );
    }
    return (
      <span className="inline-flex w-full items-center gap-1">
        <input
          ref={ref as React.RefObject<HTMLInputElement>}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          className={cn(
            'min-w-0 flex-1 rounded border border-[var(--nn-blue)] bg-[var(--nn-blue-pale)] px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--nn-blue-light)]',
            inputClassName,
          )}
        />
        <button type="button" onClick={commit} className="shrink-0 rounded p-1 text-green-600 hover:bg-green-50" aria-label="Сохранить">
          <Check size={14} />
        </button>
        <button type="button" onClick={cancel} className="shrink-0 rounded p-1 text-red-500 hover:bg-red-50" aria-label="Отмена">
          <X size={14} />
        </button>
      </span>
    );
  }

  if (multiline) {
    return (
      <span
        className={cn('group relative block w-full cursor-pointer', className)}
        onClick={() => setEditing(true)}
        title="Нажмите для редактирования"
      >
        <Pencil
          size={11}
          strokeWidth={2}
          className="pointer-events-none absolute top-0.5 right-0 z-[1] shrink-0 text-[var(--nn-blue)] opacity-0 transition-opacity group-hover:opacity-60"
          aria-hidden
        />
        <Tag className="block pr-5">
          {value || <span className="text-muted-foreground italic">{placeholder}</span>}
        </Tag>
      </span>
    );
  }

  return (
    <span
      className={cn('group flex w-full min-w-0 cursor-pointer items-start gap-1', className)}
      onClick={() => setEditing(true)}
      title="Нажмите для редактирования"
    >
      <Tag
        className={cn(
          'min-w-0 flex-1',
          clampLines === 1 && 'line-clamp-1',
          clampLines === 2 && 'line-clamp-2',
          clampLines === 3 && 'line-clamp-3',
        )}
      >
        {value || <span className="text-muted-foreground italic">{placeholder}</span>}
      </Tag>
      <Pencil
        size={11}
        strokeWidth={2}
        className="mt-0.5 shrink-0 text-[var(--nn-blue)] opacity-0 transition-opacity group-hover:opacity-60"
        aria-hidden
      />
    </span>
  );
}

// ─── InlineNumber ─────────────────────────────────────────────────────────

interface InlineNumberProps {
  value: number;
  onSave: (v: number) => void;
  min?: number;
  max?: number;
  suffix?: string;
  className?: string;
  readOnly?: boolean;
}

export function InlineNumber({ value, onSave, min = 0, max = 100, suffix = '', className, readOnly = false }: InlineNumberProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => { setDraft(String(value)); }, [value]);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  const commit = () => {
    const n = Math.min(max, Math.max(min, Number(draft) || value));
    onSave(n);
    setEditing(false);
  };

  if (readOnly) {
    return (
      <span className={cn('inline-flex items-center gap-1 cursor-default', className)}>
        <span>{value}{suffix}</span>
      </span>
    );
  }

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input
          ref={ref}
          type="number"
          value={draft}
          min={min}
          max={max}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(String(value)); setEditing(false); } }}
          className="w-20 rounded border border-[var(--nn-blue)] bg-[var(--nn-blue-pale)] px-2 py-0.5 text-sm focus:outline-none"
        />
        {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
        <button onClick={commit} className="p-1 rounded text-green-600 hover:bg-green-50"><Check size={12} /></button>
      </span>
    );
  }

  return (
    <span
      className={cn('group relative inline-flex cursor-pointer items-center', className)}
      onClick={() => setEditing(true)}
    >
      <span className="tabular-nums">
        {value}
        {suffix}
      </span>
      <Pencil
        size={11}
        className="pointer-events-none absolute left-full top-1/2 z-[1] ml-1 -translate-y-1/2 text-[var(--nn-blue)] opacity-0 transition-opacity group-hover:opacity-60"
        aria-hidden
      />
    </span>
  );
}

// ─── InlineDate ───────────────────────────────────────────────────────────

interface InlineDateProps {
  value: string;
  onSave: (v: string) => void;
  label?: string;
  readOnly?: boolean;
}

export function InlineDate({ value, onSave, label, readOnly = false }: InlineDateProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  const fmt = (d: string) => {
    try { return new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  if (readOnly) {
    return (
      <span className="inline-flex items-center gap-1 cursor-default">
        {label && <span className="text-xs text-muted-foreground">{label}:</span>}
        <span className="text-sm font-medium">{fmt(value)}</span>
      </span>
    );
  }

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        {label && <span className="text-xs text-muted-foreground">{label}:</span>}
        <input
          ref={ref}
          type="date"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { onSave(draft); setEditing(false); } if (e.key === 'Escape') setEditing(false); }}
          className="rounded border border-[var(--nn-blue)] bg-[var(--nn-blue-pale)] px-2 py-0.5 text-sm focus:outline-none"
        />
        <button type="button" onClick={() => { onSave(draft); setEditing(false); }} className="p-1 rounded text-green-600 hover:bg-green-50"><Check size={12} /></button>
        <button type="button" onClick={() => setEditing(false)} className="p-1 rounded text-red-500 hover:bg-red-50"><X size={12} /></button>
      </span>
    );
  }

  return (
    <span className="group inline-flex items-center gap-1 cursor-pointer" onClick={() => setEditing(true)}>
      {label && <span className="text-xs text-muted-foreground">{label}:</span>}
      <span className="text-sm font-medium">{fmt(value)}</span>
      <Pencil size={11} className="opacity-0 group-hover:opacity-60 text-[var(--nn-blue)] transition-opacity" />
    </span>
  );
}

// ─── InlineSelect ─────────────────────────────────────────────────────────

interface InlineSelectProps<T extends string> {
  value: T;
  options: { value: T; label: string }[];
  onSave: (v: T) => void;
  className?: string;
  renderValue?: (v: T) => React.ReactNode;
  readOnly?: boolean;
}

export function InlineSelect<T extends string>({ value, options, onSave, className, renderValue, readOnly = false }: InlineSelectProps<T>) {
  const [editing, setEditing] = useState(false);

  if (readOnly) {
    return (
      <span className={cn('inline-flex items-center gap-1 cursor-default', className)}>
        {renderValue ? renderValue(value) : <span className="text-sm">{options.find(o => o.value === value)?.label ?? value}</span>}
      </span>
    );
  }

  if (editing) {
    return (
      <select
        autoFocus
        value={value}
        onChange={e => { onSave(e.target.value as T); setEditing(false); }}
        onBlur={() => setEditing(false)}
        className="rounded border border-[var(--nn-blue)] bg-[var(--nn-blue-pale)] px-2 py-0.5 text-sm focus:outline-none"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }

  return (
    <span
      className={cn('group relative inline-flex cursor-pointer items-center', className)}
      onClick={() => setEditing(true)}
    >
      {renderValue ? renderValue(value) : <span className="text-sm">{options.find(o => o.value === value)?.label ?? value}</span>}
      <Pencil
        size={11}
        className="pointer-events-none absolute left-full top-1/2 z-[1] ml-1 -translate-y-1/2 text-[var(--nn-blue)] opacity-0 transition-opacity group-hover:opacity-60"
        aria-hidden
      />
    </span>
  );
}
